/** The longest side a receipt photo is sent at: enough to read, far less to upload. */
export const RECEIPT_MAX_SIDE = 2000;
const JPEG_QUALITY = 0.85;

/** The image types every browser here can draw on a canvas; HEIC and PDF go as they are. */
const DRAWABLE = new Set(['image/jpeg', 'image/png', 'image/webp']);

export interface PreparedReceipt {
  blob: Blob;
  name: string;
}

/**
 * A big phone photo, downscaled to {@link RECEIPT_MAX_SIDE} as a JPEG before
 * upload. Anything else (a PDF, a HEIC the browser cannot draw, a photo
 * already small, or a failure along the way) is sent unchanged: the server
 * checks the bytes either way.
 */
export async function prepareReceipt(file: File, maxSide = RECEIPT_MAX_SIDE): Promise<PreparedReceipt> {
  const unchanged = { blob: file, name: file.name || 'receipt' };
  if (!DRAWABLE.has(file.type) || typeof createImageBitmap !== 'function') return unchanged;

  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    if (scale === 1) return unchanged;

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const context = canvas.getContext('2d');
    if (!context) return unchanged;
    // A transparent PNG would turn black as a JPEG.
    context.fillStyle = '#fff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    if (!blob || blob.size >= file.size) return unchanged;
    return { blob, name: file.name.replace(/\.[^.]*$/, '') + '.jpg' };
  } catch {
    return unchanged;
  } finally {
    bitmap?.close();
  }
}
